// Encode settings shared by the encode editor and the encoding template
// editor (EncodeSettings.vue): the form's defaults and what the API takes.
import type { Audio, Destination, Video } from './api'
import { verticalBody } from './vertical'
import { ladderBody } from './ladder'

// Optional video fields are left out of saved encodes; the selects use ''
// for "same as input", "auto", "bicubic" and "default", and an undefined
// value matches no option (the select then showed blank)
export function formDefaults<T extends { video: Video }>(e: T): T {
    const v = e.video
    v.fps ??= ''
    v.pix_fmt ??= ''
    v.colour ??= ''
    v.level ??= ''
    v.scale_algo ??= ''
    v.profile ??= ''
    v.preset ||= defaultPreset(v.codec)
    return e
}
export const defaultPreset = (codec: string) =>
    codec.endsWith('nvenc') ? 'p4' : codec === 'libsvtav1' ? '8' : 'veryfast'

// the form's video and audio as the API takes them (numbers, empty fields
// left out); a passthrough copies its source, so it has no bitrate of its
// own: its bitrate_k is the source's rate its delay buffer is sized for
// (bufferK; 0 = 25000 until the node has measured the source)
export function apiEncoding(
    v: Video,
    audio: Audio[],
    advanced: string | undefined,
    passthrough = false,
    bufferK: number | string = 0,
) {
    return {
        video: {
            ...v,
            gpu: Number(v.gpu),
            bitrate_k: passthrough ? Number(bufferK) || 0 : Number(v.bitrate_k),
            maxrate_k: v.maxrate_k ? Number(v.maxrate_k) : undefined,
            bufsize_k: v.bufsize_k ? Number(v.bufsize_k) : undefined,
            gop: Number(v.gop),
            bframes: Number(v.bframes),
            width: v.width ? Number(v.width) : undefined,
            height: v.height ? Number(v.height) : undefined,
            fps: v.fps || undefined,
            pix_fmt: v.pix_fmt || undefined,
            colour: v.colour || undefined,
            master_display: v.master_display?.trim() || undefined,
            max_cll: v.max_cll?.trim() || undefined,
            scale_algo: v.scale_algo || undefined,
            profile: v.profile || undefined,
            level: v.level || undefined,
            preset: v.preset || undefined,
            vertical: passthrough ? undefined : verticalBody(v.vertical),
            ladder: passthrough ? undefined : ladderBody(v.ladder),
        },
        audio: audio.map((a) => ({
            ...a,
            name: a.name || undefined,
            bitrate_k: Number(a.bitrate_k),
            channel_map: a.channel_map.map(Number),
        })),
        advanced: advanced || undefined,
    }
}

// The encode lab's guidance (ENCODE-LAB-2026-10-05.md, -10-09.md), as
// settings the form fills in and the operator can still change: nothing is
// chosen behind their back (DECISIONS 2026-10-09 "encode guidance").

// NVENC H.264 for platforms: p7, 3 B-frames, lookahead with its adaptive
// B-frames held fixed (they lost the bursts), temporal AQ, and no key
// frames at scene cuts: with lookahead NVENC puts an IDR at a cut and
// starts its GOP over, off the 2 s cadence platforms cut at (the lab
// measured it costs at most 0.2 VMAF; Twitch EB does the same, TW-5).
// About 0.5 s more delay (32 frames at 60 fps); GPU time, no CPU.
export const nvencTuning = ['-rc-lookahead', '32', '-b_adapt', '0', '-temporal-aq', '1', '-no-scenecut', '1']
const lookaheadFlags = ['-rc-lookahead', '-b_adapt', '-no-scenecut']

// the "-flag value" pairs of a tuning string, in order
function pairs(advanced: string | undefined): [string, string][] {
    const words = (advanced ?? '').trim().split(/\s+/).filter(Boolean)
    const out: [string, string][] = []
    for (let i = 0; i < words.length; i++) {
        const w = words[i]!
        if (w.startsWith('-') && i + 1 < words.length && !words[i + 1]!.startsWith('-')) out.push([w, words[++i]!])
        else out.push([w, ''])
    }
    return out
}
const joinPairs = (p: [string, string][]) => p.map(([f, v]) => (v ? `${f} ${v}` : f)).join(' ')

// withTuning sets each "-flag value" of tuning in advanced: a flag already
// there takes the new value, the rest of advanced stays as it was
export function withTuning(advanced: string | undefined, tuning: string[]): string {
    const p = pairs(advanced)
    for (let i = 0; i < tuning.length; i += 2) {
        const [f, v] = [tuning[i]!, tuning[i + 1]!]
        const at = p.findIndex(([g]) => g === f)
        if (at >= 0) p[at] = [f, v]
        else p.push([f, v])
    }
    return joinPairs(p)
}
// withoutLookahead drops the lookahead flags (a contribution link's delay)
export const withoutLookahead = (advanced: string | undefined) =>
    joinPairs(pairs(advanced).filter(([f]) => !lookaheadFlags.includes(f)))
export const hasLookahead = (advanced: string | undefined) => pairs(advanced).some(([f]) => f === '-rc-lookahead')

// Low delay for a contribution link (DECISIONS 2026-10-10 "SRT latency"):
// no B-frames (each held the frames after it back) and the encoder's own
// zero-latency tuning: NVENC's ultra-low-latency, frames out as they are
// made (the caster return feed's settings), x264's and x265's
// zerolatency. An option the operator applies, never a default: the same
// rate buys a little less picture (DECISIONS 2026-10-03 "low latency, as
// options"). SVT-AV1 has none to offer.
export function lowDelayTuning(codec: string): string[] {
    if (codec.endsWith('_nvenc')) return ['-tune', 'ull', '-zerolatency', '1', '-delay', '0']
    if (codec === 'libx264' || codec === 'libx265') return ['-tune', 'zerolatency']
    return []
}
// lowDelay: the encode with low delay's settings, its other tuning kept (a
// lookahead goes: it holds frames back too)
export function lowDelay(v: Video, advanced: string | undefined): { video: Video; advanced: string } {
    return { video: { ...v, bframes: 0 }, advanced: withTuning(withoutLookahead(advanced), lowDelayTuning(v.codec)) }
}
export function isLowDelay(v: Video, advanced: string | undefined): boolean {
    const t = lowDelayTuning(v.codec)
    const p = new Map(pairs(advanced))
    return (
        t.length > 0 &&
        Number(v.bframes) === 0 &&
        !hasLookahead(advanced) &&
        t.every((f, i) => i % 2 === 1 || p.get(f) === t[i + 1])
    )
}

// isRecommended: an NVENC H.264 encode on the lab's platform settings
export function isRecommended(v: Video, advanced: string | undefined): boolean {
    if (v.codec !== 'h264_nvenc') return false
    const p = new Map(pairs(advanced))
    return (
        v.preset === 'p7' &&
        Number(v.bframes) === 3 &&
        nvencTuning.every((f, i) => i % 2 === 1 || p.get(f) === nvencTuning[i + 1])
    )
}
// recommended: the lab's platform settings for NVENC H.264 on this encode
// (from x264 too: NVENC takes 4:2:0 in 8 bits only, no x264-only profile)
export function recommended(v: Video, advanced: string | undefined): { video: Video; advanced: string } {
    return {
        video: {
            ...v,
            codec: 'h264_nvenc',
            preset: 'p7',
            bframes: 3,
            profile: ['', 'baseline', 'main', 'high'].includes(v.profile ?? '') ? v.profile : '',
            pix_fmt: v.pix_fmt === 'nv12' ? v.pix_fmt : '',
        },
        advanced: withTuning(v.codec === 'h264_nvenc' ? advanced : x264Free(advanced), nvencTuning),
    }
}
// tuning only x264 takes is left behind when an encode moves to NVENC
const x264Free = (advanced: string | undefined) =>
    joinPairs(pairs(advanced).filter(([f]) => !['-x264-params', '-x264opts'].includes(f)))

// Which guidance an encode's destinations call for: platforms (Twitch,
// YouTube, HLS) take a little delay for a better picture; a contribution
// link (SRT, UDP, RIST, RTP, a pull) wants the lowest delay; a recording
// alone none.
const platformKinds: Destination['kind'][] = ['rtmp_push', 'twitch_eb', 'hls_push', 'hls_pull']
const contributionKinds: Destination['kind'][] = ['srt_caller', 'srt_listener', 'udp', 'rist', 'rtp', 'rtmp_pull']
export type Use = { use: 'platform' | 'contribution'; why: string }
export function destinationUse(dests: Destination[]): Use | null {
    const contribution = dests.find((d) => contributionKinds.includes(d.kind))
    if (contribution)
        return { use: 'contribution', why: `${contribution.name || contribution.id} is a contribution link` }
    const platform = dests.filter((d) => platformKinds.includes(d.kind))
    if (!platform.length) return null
    return { use: 'platform', why: `goes to ${platform.map((d) => d.name || d.id).join(', ')}` }
}

// faster: what to offer an encode that falls behind (it loses frames):
// x264 the next faster preset, and on a node with an NVIDIA GPU the lab's
// NVENC settings; NVENC p5-p7 the balanced p4. Suggestions only: the
// operator picks one (DECISIONS 2026-10-09).
const x264Order = ['veryslow', 'slower', 'slow', 'medium', 'fast', 'faster', 'veryfast', 'superfast', 'ultrafast']
export function faster(
    v: Video,
    advanced: string | undefined,
    hasNVIDIA: boolean,
): { label: string; video: Video; advanced: string }[] {
    const out: { label: string; video: Video; advanced: string }[] = []
    if (v.codec === 'libx264') {
        const next = x264Order[x264Order.indexOf(v.preset || 'veryfast') + 1]
        if (next) out.push({ label: `x264 ${next}`, video: { ...v, preset: next }, advanced: advanced ?? '' })
        if (hasNVIDIA) out.push({ label: 'NVENC, the recommended settings', ...recommended(v, advanced) })
    } else if (v.codec.endsWith('_nvenc') && ['p5', 'p6', 'p7'].includes(v.preset ?? ''))
        out.push({ label: 'NVENC p4', video: { ...v, preset: 'p4' }, advanced: advanced ?? '' })
    return out
}
